import React, { useMemo, useState } from 'react';
import { useQuery, useMutation } from 'react-apollo';
import styled from 'styled-components';
import * as colorsVariables from 'styles/abstracts/colors';
import GET_PRESENTATION_JOBS from '../../graphql/getPresentationJobs';
import DELETE_PRESENTATION_JOB from '../../graphql/deletePresentationJob';

const { grayShades } = colorsVariables;

// Rafraîchissement automatique pour suivre "en attente" -> "en cours" -> "terminé"
// sans que l'utilisateur ait à recharger la page.
const POLL_INTERVAL_MS = 12000;
// Nombre max de jobs conservés PAR statut (en attente / en cours / terminé), côté backend.
const JOBS_LIMIT_PER_STATUS = 10;
// Un job "terminé" plus vieux que ça devient supprimable (bouton Supprimer).
const DELETABLE_DONE_AFTER_HOURS = 24;

type PresentationJob = {
  jobId: string;
  status: 'requested' | 'processing' | 'succeeded' | 'failed';
  requestedAt: string | null;
  generatedAt: string | null;
  downloadUrl: string | null;
  projectName: string | null;
  templateName: string | null;
  filters: Record<string, any> | null;
  progressPercent: number | null;
  stepLabel: string | null;
  durationMinutes: number | null;
  isBlocked: boolean;
};

const Widget = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  padding: 0.6rem 0.7rem;
  min-width: 420px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  /* Aligné à droite du bandeau (à côté des annonces) */
  margin-left: auto;
`;

const WidgetHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid #e2e8f0;
  padding-bottom: 0.4rem;
  margin-bottom: 0.5rem;
`;

const WidgetTitle = styled.h4`
  font-size: 0.68rem;
  font-weight: 700;
  color: #1e293b;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin: 0;
  display: flex;
  align-items: center;

  &::before {
    content: '';
    width: 0.4rem;
    height: 0.4rem;
    border-radius: 999px;
    background: #10b981;
    margin-right: 0.4rem;
  }
`;

const ActiveBadge = styled.span`
  background: #ede9fe;
  color: #6d28d9;
  font-size: 0.6rem;
  font-weight: 600;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
`;

const ColumnsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.45rem;
`;

const statusStyles: Record<string, { bg: string; border: string; text: string; label: string; dot: string }> = {
  requested: { bg: '#ffffff', border: '#e2e8f0', text: '#b45309', label: 'En attente', dot: '#fbbf24' },
  processing: { bg: '#eef2ff', border: '#c7d2fe', text: '#4338ca', label: 'En cours', dot: '#6366f1' },
  succeeded: { bg: '#f7fdf0', border: '#c9eab0', text: '#589600', label: 'Terminé', dot: '#79ca00' },
  failed: { bg: '#fef2f2', border: '#fecaca', text: '#b91c1c', label: 'Échoué', dot: '#ef4444' },
};

const Column = styled.div<{ status: string }>`
  background: ${({ status }) => statusStyles[status].bg};
  border: 1px solid ${({ status }) => statusStyles[status].border};
  border-radius: 0.35rem;
  padding: 0.4rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 88px;
  font-size: 0.62rem;
  position: relative;
`;

const ColumnHeader = styled.div<{ status: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: ${({ status }) => statusStyles[status].text};
  font-weight: 600;
  margin-bottom: 0.3rem;
`;

const StatusDot = styled.span<{ status: string }>`
  width: 0.3rem;
  height: 0.3rem;
  border-radius: 999px;
  background: ${({ status }) => statusStyles[status].dot};
  display: inline-block;
  margin-left: 0.25rem;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  color: ${grayShades.g500};
  font-size: 0.55rem;

  button {
    background: none;
    border: none;
    cursor: pointer;
    color: inherit;
    padding: 0 0.15rem;

    &:hover {
      color: #1e293b;
    }

    &:disabled {
      opacity: 0.35;
      cursor: default;
    }
  }
`;

const JobLine = styled.p`
  font-weight: 700;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  text-decoration: underline dashed #cbd5e1;
  text-underline-offset: 2px;
  cursor: default;
  margin: 0;
`;

const SubLine = styled.p`
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin: 0.1rem 0 0 0;
`;

const EmptyState = styled.p`
  color: #94a3b8;
  font-style: italic;
  margin: auto;
`;

const GlobalEmptyState = styled.div`
  color: #94a3b8;
  font-style: italic;
  font-size: 0.68rem;
  text-align: center;
  padding: 0.5rem 0;
`;

const Footer = styled.div<{ status: string }>`
  margin-top: 0.3rem;
  padding-top: 0.25rem;
  border-top: 1px solid ${({ status }) => statusStyles[status].border};
  display: flex;
  justify-content: space-between;
  color: ${({ status }) => statusStyles[status].text};
  font-size: 0.58rem;
`;

const DownloadButton = styled.a`
  margin-top: 0.3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  background: #79ca00;
  color: white;
  font-weight: 600;
  font-size: 0.6rem;
  padding: 0.2rem 0.3rem;
  border-radius: 0.25rem;
  text-decoration: none;

  &:hover {
    opacity: 0.9;
    color: white;
  }
`;

const DeleteButton = styled.button`
  border: none;
  background: transparent;
  color: #94a3b8;
  cursor: pointer;
  font-size: 0.65rem;
  line-height: 1;
  padding: 0 0.15rem;

  &:hover {
    color: #dc2626;
  }
`;

const ProgressBarTrack = styled.div`
  width: 100%;
  height: 4px;
  border-radius: 999px;
  background: #e0e7ff;
  overflow: hidden;
  margin-top: 0.2rem;
`;

const ProgressBarFill = styled.div<{ percent: number }>`
  height: 100%;
  border-radius: 999px;
  background: #6366f1;
  width: ${({ percent }) => percent}%;
  transition: width 0.6s ease;
`;

const SupportAlert = styled.span`
  color: #dc2626;
  font-weight: 700;
`;

const Tooltip = styled.div`
  position: absolute;
  left: 0;
  bottom: 100%;
  margin-bottom: 0.3rem;
  z-index: 50;
  width: 15rem;
  background: #0f172a;
  color: white;
  border-radius: 0.35rem;
  padding: 0.4rem 0.5rem;
  font-size: 0.6rem;
  line-height: 1.3;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
  display: none;
`;

const TooltipWrapper = styled.div`
  position: relative;

  &:hover ${Tooltip} {
    display: block;
  }
`;

// En dessous de 24h: heure ("14:32"). Au-delà: date complète ("le 24/12/2026"),
// pour éviter d'afficher une heure trompeuse sur un job vieux de plusieurs jours.
const formatRelativeDate = (isoString: string | null): { usesDate: boolean; text: string } => {
  if (!isoString) return { usesDate: false, text: '-' };
  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) return { usesDate: false, text: '-' };
  const diffHours = (Date.now() - date.getTime()) / (1000 * 60 * 60);
  if (diffHours >= 24) {
    return { usesDate: true, text: date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) };
  }
  return { usesDate: false, text: date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) };
};

const formatPeriod = (filters: Record<string, any> | null): string => {
  if (!filters?.start_date || !filters?.end_date) return 'N/A';
  return `${filters.start_date} → ${filters.end_date}`;
};

const formatCriteriaKeys = (filters: Record<string, any> | null): string => {
  const criteriaKeys = filters?.criteria_keys;
  if (!Array.isArray(criteriaKeys) || criteriaKeys.length === 0) return 'Aucun';
  return criteriaKeys.map(criteria => `${criteria.name}: ${(criteria.values || []).join(', ')}`).join(' • ');
};

const formatThematics = (filters: Record<string, any> | null): string => {
  const thematics = filters?.thematics;
  if (!Array.isArray(thematics) || thematics.length === 0) return 'Aucune';
  return thematics
    .map(thematic => [thematic.thematic, thematic.subThematic, thematic.subSubThematic].filter(Boolean).join(' > '))
    .join(' • ');
};

const shortJobId = (jobId: string): string => (jobId?.length > 12 ? `${jobId.slice(0, 11)}` : jobId);

// Un job est supprimable s'il est: en attente, bloqué (en cours depuis trop longtemps),
// ou terminé/échoué depuis plus de DELETABLE_DONE_AFTER_HOURS.
const isDeletable = (job: PresentationJob): boolean => {
  if (job.status === 'requested') return true;
  if (job.status === 'processing') return !!job.isBlocked;
  if (job.status === 'succeeded' || job.status === 'failed') {
    if (!job.generatedAt) return true;
    const hoursSince = (Date.now() - new Date(job.generatedAt).getTime()) / (1000 * 60 * 60);
    return hoursSince >= DELETABLE_DONE_AFTER_HOURS;
  }
  return false;
};

const JobCard = ({ job, onDelete }: { job: PresentationJob; onDelete: (job: PresentationJob) => void }) => {
  const style = statusStyles[job.status];
  const templateName = job.filters?.template_name || job.templateName || 'Template';
  const projectName = job.projectName || 'Projet';
  const tooltipAlt = job.isBlocked
    ? 'Ce job semble bloqué. '
    : '';

  return (
    <TooltipWrapper>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <JobLine title={`${job.jobId} - Survolez pour voir les détails`}>{shortJobId(job.jobId)}</JobLine>
        {isDeletable(job) && (
          <DeleteButton
            type="button"
            title="Supprimer ce rapport de la liste"
            onClick={e => {
              e.stopPropagation();
              onDelete(job);
            }}
          >
            ✕
          </DeleteButton>
        )}
      </div>
      <SubLine>{projectName}</SubLine>
      <SubLine>{templateName}</SubLine>
      <Tooltip>
        <div
          style={{
            fontWeight: 700,
            borderBottom: '1px solid #334155',
            paddingBottom: '0.2rem',
            marginBottom: '0.2rem',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <span>{job.jobId}</span>
          <span style={{ color: style.dot, fontWeight: 400 }}>{style.label}</span>
        </div>
        {job.isBlocked && (
          <p style={{ margin: '0 0 0.2rem 0' }}>
            <SupportAlert>⚠ Contacter le support</SupportAlert>
            <span> — {tooltipAlt}aucune mise à jour depuis longtemps.</span>
          </p>
        )}
        <p style={{ margin: 0 }}>
          <span style={{ color: '#94a3b8' }}>Template : </span>
          {templateName}
        </p>
        <p style={{ margin: 0 }}>
          <span style={{ color: '#94a3b8' }}>Projet : </span>
          {projectName}
        </p>
        <p style={{ margin: 0 }}>
          <span style={{ color: '#94a3b8' }}>Période : </span>
          {formatPeriod(job.filters)}
        </p>
        <p style={{ margin: 0 }}>
          <span style={{ color: '#94a3b8' }}>Critères clés : </span>
          {formatCriteriaKeys(job.filters)}
        </p>
        <p style={{ margin: 0 }}>
          <span style={{ color: '#94a3b8' }}>Thématiques : </span>
          {formatThematics(job.filters)}
        </p>
      </Tooltip>
    </TooltipWrapper>
  );
};

type StatusColumnProps = {
  fallbackStatus: string;
  jobs: PresentationJob[];
  queuedCount?: number;
  onDelete: (job: PresentationJob) => void;
};

const StatusColumn = ({ fallbackStatus, jobs, queuedCount, onDelete }: StatusColumnProps) => {
  const [index, setIndex] = useState(0);
  const total = jobs.length;
  const currentJob = total > 0 ? jobs[Math.min(index, total - 1)] : null;
  // La colonne "Terminé" peut mélanger succeeded/failed: l'en-tête reflète le job affiché.
  const status = currentJob?.status || fallbackStatus;
  const style = statusStyles[status];

  return (
    <Column status={status}>
      <div>
        <ColumnHeader status={status}>
          <span>
            {style.label}
            <StatusDot status={status} />
            {/* Compteur global "en attente": toute la queue, tous projets/comptes confondus */}
            {fallbackStatus === 'requested' && typeof queuedCount === 'number' && (
              <span title="Nombre total de rapports en attente dans la queue, tous projets confondus" style={{ marginLeft: '0.3rem' }}>
                ({queuedCount})
              </span>
            )}
            {/* % de complétion estimé pour le job "en cours" affiché */}
            {status === 'processing' && currentJob && !currentJob.isBlocked && (
              <span style={{ marginLeft: '0.3rem', fontWeight: 400 }}>{currentJob.progressPercent ?? 0}%</span>
            )}
            {status === 'processing' && currentJob?.isBlocked && (
              <SupportAlert title="Contacter le support" style={{ marginLeft: '0.3rem' }}>
                ⚠ Support
              </SupportAlert>
            )}
            {/* Durée (minutes) pour le job "terminé" affiché */}
            {status === 'succeeded' && currentJob?.durationMinutes != null && (
              <span style={{ marginLeft: '0.3rem', fontWeight: 400 }}>({currentJob.durationMinutes} min)</span>
            )}
          </span>
          {total > 1 && (
            <PaginationControls>
              <button type="button" onClick={() => setIndex(i => Math.max(0, i - 1))} disabled={index === 0} title="Précédent">
                &lt;
              </button>
              <span>
                {index + 1}/{total}
              </span>
              <button
                type="button"
                onClick={() => setIndex(i => Math.min(total - 1, i + 1))}
                disabled={index === total - 1}
                title="Suivant"
              >
                &gt;
              </button>
            </PaginationControls>
          )}
        </ColumnHeader>
        {currentJob ? <JobCard job={currentJob} onDelete={onDelete} /> : <EmptyState>Aucun rapport</EmptyState>}
        {status === 'processing' && currentJob && (
          <ProgressBarTrack title={currentJob.stepLabel || ''}>
            <ProgressBarFill percent={currentJob.isBlocked ? 100 : currentJob.progressPercent ?? 0} />
          </ProgressBarTrack>
        )}
      </div>
      {currentJob &&
        (() => {
          const isDone = status === 'succeeded' || status === 'failed';
          const { usesDate, text } = formatRelativeDate(isDone ? currentJob.generatedAt : currentJob.requestedAt);
          const baseLabel = isDone ? style.label : 'Lancé';
          return (
            <Footer status={status}>
              <span>{status === 'processing' && currentJob.stepLabel ? currentJob.stepLabel : usesDate ? `${baseLabel} le` : `${baseLabel} à`}</span>
              <span>{text}</span>
            </Footer>
          );
        })()}
      {currentJob?.status === 'succeeded' && currentJob.downloadUrl && (
        <DownloadButton href={currentJob.downloadUrl} target="_blank" rel="noreferrer noopener" title="Télécharger le fichier">
          Télécharger
        </DownloadButton>
      )}
    </Column>
  );
};

type PresentationsLaunchedWidgetProps = {
  projectId?: number;
};

// BEGIN: PresentationsLaunchedWidget (User requested prominent feature)
const PresentationsLaunchedWidget = ({ projectId }: PresentationsLaunchedWidgetProps) => {
  const { data, refetch } = useQuery(GET_PRESENTATION_JOBS, {
    variables: { projectId, limit: JOBS_LIMIT_PER_STATUS },
    // Uniquement les rapports du projet actuellement affiché, lancés par l'utilisateur courant
    // (filtrage déjà appliqué côté backend, cf. presentation_jobs resolver).
    skip: !projectId,
    pollInterval: POLL_INTERVAL_MS,
    fetchPolicy: 'cache-and-network',
  });

  const [deletePresentationJob] = useMutation(DELETE_PRESENTATION_JOB);

  const jobs: PresentationJob[] = data?.presentationJobs || [];
  const queuedCount: number = data?.queuedPresentationJobsCount ?? 0;

  const { pending, inProgress, done } = useMemo(() => {
    return {
      pending: jobs.filter(job => job.status === 'requested'),
      inProgress: jobs.filter(job => job.status === 'processing'),
      // "Terminé" regroupe succeeded + failed (les échecs restent visibles, colorés différemment)
      done: jobs.filter(job => job.status === 'succeeded' || job.status === 'failed'),
    };
  }, [jobs]);

  // Job actif = en attente + en cours (ni terminé, ni échoué)
  const activeJobsCount = pending.length + inProgress.length;

  const handleDelete = async (job: PresentationJob) => {
    try {
      await deletePresentationJob({ variables: { jobId: job.jobId, projectId } });
      refetch();
    } catch (e) {
      // Best-effort: le prochain rafraîchissement automatique (pollInterval) corrigera l'affichage
    }
  };

  if (!projectId) {
    return null;
  }

  return (
    <Widget id="widget-presentations-lancees" data-component="presentations-tracker">
      <WidgetHeader>
        <WidgetTitle>Rapport(s) standard(s)</WidgetTitle>
        <ActiveBadge>
          {activeJobsCount} job{activeJobsCount > 1 ? 's' : ''} actif{activeJobsCount > 1 ? 's' : ''}
        </ActiveBadge>
      </WidgetHeader>
      {jobs.length === 0 ? (
        <GlobalEmptyState>Vous n'avez pas encore lancé de rapport standard sur ce projet.</GlobalEmptyState>
      ) : (
        <ColumnsGrid>
          <StatusColumn fallbackStatus="requested" jobs={pending} queuedCount={queuedCount} onDelete={handleDelete} />
          <StatusColumn fallbackStatus="processing" jobs={inProgress} onDelete={handleDelete} />
          <StatusColumn fallbackStatus="succeeded" jobs={done} onDelete={handleDelete} />
        </ColumnsGrid>
      )}
    </Widget>
  );
};
// END: PresentationsLaunchedWidget

export default PresentationsLaunchedWidget;
